import { create } from 'zustand';

export interface CarritoItem {
  /** ID temporal client-side (uuid corto). */
  uid: string;
  productoId?: number;
  paqueteId?: number;
  nombre: string;
  cantidad: number;
  /**
   * Sprint 131 — Venta al peso: lo que pesa este renglón, en la unidad base del producto.
   *
   * `undefined` en la venta normal por formato, que es casi todo lo que se vende.
   */
  cantidadMedida?: number;
  precioUnitario: number;
  notasEspeciales?: string;
  /** Sprint 7.4. Selección de producto final por renglón cuando es paquete. */
  paqueteProductos?: Array<{
    paqueteProductoId: number;
    productoFinalId: number;
    productoNombre: string;
    posicion: number;
    /** Sprint 44. Si true, el cliente pidió excluir este renglón. */
    excluido?: boolean;
  }>;
  /**
   * Sprint 47 — Cantidad máxima ordenable (stock disponible al momento de agregar).
   * null = producto sin tracking de insumo (sin límite).
   * undefined = no informado (sin límite).
   */
  stockMax?: number | null;
}

interface CarritoState {
  items: CarritoItem[];
  agregarProducto: (item: Omit<CarritoItem, 'uid' | 'cantidad'>) => void;
  /**
   * Sprint 131 — Una pesada es un renglón propio y **nunca se fusiona**.
   *
   * `agregarProducto` suma cantidad cuando reconoce el mismo `productoId`, y eso aquí sería un
   * desastre: pesar 350 g y luego 500 g del mismo sirloin daría "2 ×" de un renglón con un solo
   * peso, y el inventario descontaría el doble de uno y nada del otro.
   */
  agregarAlPeso: (item: Omit<CarritoItem, 'uid' | 'cantidad'>) => void;
  agregarPaquete: (item: Omit<CarritoItem, 'uid' | 'cantidad'>) => void;
  setCantidad: (uid: string, cantidad: number) => void;
  quitar: (uid: string) => void;
  setNotas: (uid: string, notas: string) => void;
  limpiar: () => void;
  subtotal: () => number;
}

const uid = (): string => Math.random().toString(36).slice(2, 10);

export const useCarritoStore = create<CarritoState>((set, get) => ({
  items: [],

  /**
   * Sprint 139 — Una arrachera término medio y otra bien cocida son **dos renglones**.
   *
   * El carrito fusionaba por `productoId`, así que tocar el producto dos veces subía la cantidad
   * a 2 y la nota quedaba compartida: el sistema no tenía forma de decir *"una media y una bien
   * cocida"* — que es exactamente el caso que el Sprint 138 venía a resolver. Peor: corregir el
   * término del segundo le cambiaba el plato al primero, sin un solo aviso, y la comanda salía
   * con `2x Arrachera / Nota: bien cocido`.
   *
   * La regla nueva: **un renglón anotado ya no se fusiona.** El siguiente toque abre su propio
   * renglón, con su propia nota. Los renglones sin nota siguen fusionándose como siempre —que es
   * lo que hace rápida la venta normal, y el 99 % de los toques.
   *
   * Lo cazó `qa-chaos` sobre el store real, no la suite: los tests del carrito probaban un
   * renglón a la vez.
   */
  agregarProducto: (item) => {
    set((state) => {
      const existente = state.items.find(
        (i) =>
          i.productoId === item.productoId &&
          !i.paqueteProductos &&
          // Con nota, cada renglón es un plato distinto aunque el producto sea el mismo.
          !i.notasEspeciales &&
          i.cantidadMedida === undefined,
      );
      if (existente) {
        return {
          items: state.items.map((i) =>
            i.uid === existente.uid ? { ...i, cantidad: i.cantidad + 1 } : i,
          ),
        };
      }
      return { items: [...state.items, { ...item, uid: uid(), cantidad: 1 }] };
    });
  },

  agregarAlPeso: (item) => {
    set((state) => ({
      items: [...state.items, { ...item, uid: uid(), cantidad: 1 }],
    }));
  },

  agregarPaquete: (item) => {
    set((state) => ({
      items: [...state.items, { ...item, uid: uid(), cantidad: 1 }],
    }));
  },

  setCantidad: (uidParam, cantidad) => {
    if (cantidad < 1) {
      set((state) => ({ items: state.items.filter((i) => i.uid !== uidParam) }));
      return;
    }
    set((state) => ({
      items: state.items.map((i) => (i.uid === uidParam ? { ...i, cantidad } : i)),
    }));
  },

  quitar: (uidParam) => {
    set((state) => ({ items: state.items.filter((i) => i.uid !== uidParam) }));
  },

  /*
    Sprint 138 — Quitar la nota la borra de verdad.

    Una cadena vacía viajaría al servidor y se guardaría como una nota en blanco: el renglón
    quedaría marcado como "anotado" sin decir nada, y el operador vería un recuadro naranja vacío
    en el carrito. Vacío es `undefined`, que es lo que significa "no hay nota".
  */
  setNotas: (uidParam, notas) => {
    const limpio = notas.trim();
    set((state) => {
      const objetivo = state.items.find((i) => i.uid === uidParam);
      if (!objetivo) return state;

      /**
       * Sprint 139 — Anotar un renglón de varios lo **separa**.
       *
       * El otro lado del mismo defecto: con "2 × Arrachera" en un solo renglón, escribir
       * "término medio" se lo ponía **a las dos**. Y es el caso más común de todos: se toca dos
       * veces el producto y después se anota.
       *
       * Así que anotar separa una unidad: queda un renglón de 1 con su nota y otro con el resto,
       * listo para recibir la suya. Es lo que hace cualquier POS de carnes y es lo que el
       * operador espera ver — dos platos, dos renglones.
       */
      if (limpio !== '' && objetivo.cantidad > 1 && !objetivo.notasEspeciales) {
        const resto = { ...objetivo, uid: uid(), cantidad: objetivo.cantidad - 1 };
        return {
          items: state.items.flatMap((i) =>
            i.uid === uidParam
              ? [{ ...i, cantidad: 1, notasEspeciales: limpio }, resto]
              : [i],
          ),
        };
      }

      return {
        items: state.items.map((i) =>
          i.uid === uidParam ? { ...i, notasEspeciales: limpio === '' ? undefined : limpio } : i,
        ),
      };
    });
  },

  limpiar: () => set({ items: [] }),

  subtotal: () => {
    return get().items.reduce((acc, i) => acc + i.precioUnitario * i.cantidad, 0);
  },
}));
